import { Divider, Menu, MenuList, Select, Text, useOutsideClick, VStack } from '@chakra-ui/react';
import React, { useCallback, useEffect, useState } from 'react';
import { ChakraSelectButton } from '../../../common/ChakraSelect/ChakraSelectButton';
import {
  getUpdatedObjectFilterChildrenAgeParams,
  getUpdatedObjectFilterGuestsParams,
} from '../../../../services/Objects/GuestsSelectorService';
import { ObjectsParams } from '../../../../types/ParamsTypes';
import { Stepper } from '../../../common/Stepper/Stepper';
import { CtaFilterButtons } from '../CtaFilterButtons/CtaFilterButtons';
import { DataOptions, getDataOptions } from '../../../../services/DataOption/DataOptionsService';

export enum GuestType {
  ADULTS = 'ADULTS',
  CHILDREN = 'CHILDREN',
  PETS = 'PETS',
}

export interface GuestsSelectorProps {
  objectsParams: ObjectsParams;
  onFilterChange: (objectsParams: ObjectsParams) => void;
}

export const GuestsSelector = (props: GuestsSelectorProps): JSX.Element => {
  const { objectsParams, onFilterChange } = props;

  const [information, setInformation] = useState<string>('Beliebig');
  const [internalObjectsParams, setInternalObjectsParams] = useState<ObjectsParams>(objectsParams);
  const { numberOfAdults, numberOfChildren, numberOfPets } = internalObjectsParams;
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [dataOptions] = useState<DataOptions>(getDataOptions());
  const ref = React.useRef();

  useOutsideClick({
    // @ts-ignore
    ref: ref,
    handler: () => setIsOpen(false),
  });

  const handleChange = useCallback(
    (guestType, value, type) => {
      const result = getUpdatedObjectFilterGuestsParams(
        internalObjectsParams,
        guestType,
        value,
        type,
      );
      setInternalObjectsParams(result);
    },
    [internalObjectsParams],
  );

  const handleChildrenChange = useCallback(
    (event, idx) => {
      const result = getUpdatedObjectFilterChildrenAgeParams(
        internalObjectsParams,
        parseInt(event.currentTarget.value, 10),
        idx,
      );
      setInternalObjectsParams(result);
    },
    [internalObjectsParams],
  );

  const handleSubmit = useCallback(() => {
    onFilterChange(internalObjectsParams);
    setIsOpen(false);
  }, [internalObjectsParams, setIsOpen, onFilterChange]);

  const handleCancel = useCallback(() => {
    setIsOpen(false);
    setInternalObjectsParams(objectsParams);
  }, [setIsOpen, objectsParams]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
  }, [setIsOpen]);

  useEffect(() => {
    if (
      objectsParams.numberOfAdults > 0 ||
      objectsParams.numberOfChildren > 0 ||
      objectsParams.numberOfPets > 0
    ) {
      setInformation(
        `${objectsParams.numberOfAdults} Erwachsene ${!dataOptions.disableChildren ? `· ${objectsParams.numberOfChildren} Kinder`: ``} ${!dataOptions.disablePets ? `· ${objectsParams.numberOfPets} Haustiere`: ``}`,
      );
    } else {
      setInformation('Beliebig');
    }
  }, [objectsParams.numberOfAdults, objectsParams.numberOfChildren, objectsParams.numberOfPets]);

  useEffect(() => {
    setInternalObjectsParams(objectsParams);
  }, [objectsParams]);

  return (
    <Menu matchWidth={true} isOpen={isOpen} onOpen={handleOpen} onClose={handleCancel}>
      <ChakraSelectButton headline={'Wer reist mit?'} information={information} />
      <MenuList p={4}>
        <VStack
          alignItems={'flex-start'}
          // @ts-ignore
          ref={ref}>
          <Stepper
            title={'Erwachsene'}
            hint={'Ab ' + dataOptions.fromAgeAdult + ' Jahren'}
            handleChange={handleChange}
            name={GuestType.ADULTS}
            value={numberOfAdults ? numberOfAdults : 0}
            minimumValue={1}
            boxSize={10}
          />
          { !dataOptions.disableChildren && <Stepper
            title={'Kinder'}
            handleChange={handleChange}
            name={GuestType.CHILDREN}
            value={numberOfChildren ? numberOfChildren : 0}
            boxSize={10}
          /> }
          {numberOfChildren > 0 && (
            <VStack w={'100%'}>
              <Text
                alignSelf={'flex-start'}
                textStyle={'guestsSelector.numberOfChildren.information'}>
                Bitte geben Sie das Alter der Kinder an
              </Text>
              {Array.from(Array(numberOfChildren)).map((a, idx) => {
                return (
                  <Select
                    key={idx}
                    userSelect={'none'}
                    onChange={e => handleChildrenChange(e, idx)}>
                    <option value={0}>0 Jahre alt</option>
                    <option value={1}>1 Jahr alt</option>
                    <option value={2}>2 Jahre alt</option>
                    <option value={3}>3 Jahre alt</option>
                    <option value={4}>4 Jahre alt</option>
                    <option value={5}>5 Jahre alt</option>
                    <option value={6}>6 Jahre alt</option>
                    <option value={7}>7 Jahre alt</option>
                    <option value={8}>8 Jahre alt</option>
                    <option value={9}>9 Jahre alt</option>
                    <option value={10}>10 Jahre alt</option>
                    <option value={11}>11 Jahre alt</option>
                    <option value={12}>12 Jahre alt</option>
                    <option value={13}>13 Jahre alt</option>
                    <option value={14}>14 Jahre alt</option>
                    <option value={15}>15 Jahre alt</option>
                    <option value={16}>16 Jahre alt</option>
                    <option value={17}>17 Jahre alt</option>
                  </Select>
                );
              })}
            </VStack>
          )}
          { !dataOptions.disablePets && <Stepper
            title={'Haustiere'}
            handleChange={handleChange}
            name={GuestType.PETS}
            value={numberOfPets ? numberOfPets : 0}
            boxSize={10}
          /> }
          <Divider />
          <CtaFilterButtons
            size={'sm'}
            handleCancel={handleCancel}
            handleFilterChange={handleSubmit}
          />
        </VStack>
      </MenuList>
    </Menu>
  );
};
